import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Autocomplete,
  Chip,
  Grid,
  Avatar,
  ListItem,
  ListItemAvatar,
  ListItemText
} from '@mui/material';
import { useParams } from 'react-router-dom';
import useProjectStore from '../../stores/projectStore';
import useTaskStore from '../../stores/taskStore';
import useUserStore from '../../stores/userStore';

// Available fields for tasks based on actual Task model
const taskFields = [
  // Basic fields
  { value: 'title', label: 'Title', type: 'text' },
  { value: 'description', label: 'Description', type: 'text' },
  { value: 'status', label: 'Status', type: 'select' },
  { value: 'priority', label: 'Priority', type: 'select' },
  { value: 'type', label: 'Task Type', type: 'select' },
  { value: 'contentStage', label: 'Content Stage', type: 'select' },
  
  // User assignments
  { value: 'assignedTo', label: 'Assigned To', type: 'user' },
  { value: 'createdBy', label: 'Created By', type: 'user' },
  { value: 'updatedBy', label: 'Updated By', type: 'user' },
  { value: 'collaborators', label: 'Collaborators', type: 'users' },
  
  // Dates
  { value: 'dueDate', label: 'Due Date', type: 'date' },
  { value: 'startedAt', label: 'Start Date', type: 'date' },
  { value: 'completedAt', label: 'Completed Date', type: 'date' },
  { value: 'createdAt', label: 'Created At', type: 'date' },
  { value: 'updatedAt', label: 'Updated At', type: 'date' },
  
  // References
  { value: 'projectId', label: 'Project', type: 'reference' },
  { value: 'sectionId', label: 'Section', type: 'reference' },
  { value: 'milestoneId', label: 'Milestone', type: 'reference' },
  { value: 'parentTaskId', label: 'Parent Task', type: 'reference' },
  { value: 'content', label: 'Content', type: 'reference' },
  
  // Time tracking
  { value: 'estimatedHours', label: 'Estimated Hours', type: 'number' },
  { value: 'actualHours', label: 'Actual Hours', type: 'number' },
  { value: 'totalTimeTracked', label: 'Total Time Tracked', type: 'number' },
  
  // Arrays/Lists
  { value: 'tags', label: 'Tags', type: 'tags' },
  { value: 'subtasks', label: 'Subtasks', type: 'array' },
  { value: 'dependencies', label: 'Dependencies', type: 'array' },
  { value: 'attachments', label: 'Attachments', type: 'array' },
  { value: 'comments', label: 'Comments', type: 'array' },
  { value: 'checklist', label: 'Checklist', type: 'array' },
  { value: 'timeEntries', label: 'Time Entries', type: 'array' },
  
  // Ordering/Position
  { value: 'orderIndex', label: 'Order Index', type: 'number' },
  { value: 'position', label: 'Position', type: 'number' },
  
  // Flags
  { value: 'isClientVisible', label: 'Is Client Visible', type: 'boolean' },
  { value: 'active', label: 'Active', type: 'boolean' }
];

// Status options from the actual Task model
const statusOptions = [
  'to_do',
  'in_progress',
  'in_review',
  'done',
  'cancelled'
];

// Priority options from the actual Task model
const priorityOptions = [
  'low',
  'medium',
  'high',
  'urgent'
];

// Task type options from the actual Task model
const taskTypeOptions = [
  'general',
  'design',
  'development',
  'content',
  'review',
  'administrative',
  'other'
];

// Content stage options from the actual Task model
const contentStageOptions = [
  'todo',
  'idea',
  'reference',
  'creative',
  'posting',
  'completed'
];

const NodeConfigDialog = ({ open, node, onClose, onSave }) => {
  const [config, setConfig] = useState({});
  const { id: projectId } = useParams();
  
  // Get project data from stores
  const { currentProject, fetchProject } = useProjectStore();
  const { projectSections: sectionsFromStore, fetchProjectSections } = useTaskStore();
  const { users, fetchUsers } = useUserStore();
  
  // State for project-specific data
  const [projectMembers, setProjectMembers] = useState([]);
  const [projectSections, setProjectSections] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (node?.data?.config) {
      setConfig(node.data.config);
    } else {
      setConfig({});
    }
  }, [node]);
  
  // Fetch project data when dialog opens
  useEffect(() => {
    if (open && projectId) {
      fetchProjectData();
    }
  }, [open, projectId]);
  
  const fetchProjectData = async () => {
    try {
      setLoading(true);
      
      // Fetch project if not already loaded
      let project = currentProject;
      if (!project || project._id !== projectId) {
        await fetchProject(projectId);
        // Get the updated project from the store after fetch
        project = useProjectStore.getState().currentProject;
      }
      
      // Fetch sections for this project
      await fetchProjectSections(projectId);
      
      // Get project members (team members only)
      if (project?.team && project.team.length > 0) {
        // Extract user objects from team members
        // team member structure is { user: { _id, name, email, ... }, permissions: {...} }
        const teamUsers = project.team
          .map(member => member.user)
          .filter(user => user && user._id); // Filter out null/undefined users
        
        setProjectMembers(teamUsers);
      } else {
        setProjectMembers([]);
      }
      
      // Set project sections from store
      const sections = sectionsFromStore?.[projectId] || [];
      setProjectSections(sections);
      
    } catch (error) {
      setProjectMembers([]);
      setProjectSections([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    // Basic validation
    const errors = [];
    
    if (node?.type === 'trigger' && !config.subtype) {
      errors.push('Please select a trigger type');
    }
    
    if (node?.type === 'condition' && (!config.field || !config.operator)) {
      errors.push('Please select a field and operator for the condition');
    }
    
    if (node?.type === 'action' && !config.subtype) {
      errors.push('Please select an action type');
    }
    
    // Specific validations
    if (config.subtype === 'webhook' && config.payload) {
      try {
        if (config.payload) JSON.parse(config.payload);
      } catch (err) {
        errors.push('Invalid JSON in webhook payload');
      }
    }
    
    if (errors.length > 0) {
      // You might want to show these errors in a snackbar or dialog
      alert('Please fix the following errors:\n' + errors.join('\n'));
      return;
    }
    
    onSave(config);
    onClose();
  };

  const renderConfigFields = () => {
    if (!node) return null;

    switch (node.type) {
      case 'trigger':
        return (
          <>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Trigger Type</InputLabel>
              <Select
                value={config.subtype || ''}
                label="Trigger Type"
                onChange={(e) => handleFieldChange('subtype', e.target.value)}
              >
                <MenuItem value="task_created">Task Created</MenuItem>
                <MenuItem value="task_updated">Task Updated</MenuItem>
                <MenuItem value="task_status_changed">Status Changed</MenuItem>
                <MenuItem value="task_assigned">Task Assigned</MenuItem>
                <MenuItem value="due_date_approaching">Due Date Approaching</MenuItem>
                <MenuItem value="field_changed">Field Changed</MenuItem>
                <MenuItem value="time_based">Scheduled</MenuItem>
                <MenuItem value="manual">Manual</MenuItem>
                <MenuItem value="webhook">Webhook</MenuItem>
              </Select>
            </FormControl>
            
            {config.subtype === 'field_changed' && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Field to Monitor</InputLabel>
                <Select
                  value={config.field || ''}
                  label="Field to Monitor"
                  onChange={(e) => handleFieldChange('field', e.target.value)}
                >
                  {taskFields.map((field) => (
                    <MenuItem key={field.value} value={field.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography>{field.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          ({field.type})
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            
            {config.subtype === 'time_based' && (
              <TextField
                fullWidth
                label="Cron Expression"
                value={config.cron || ''}
                onChange={(e) => handleFieldChange('cron', e.target.value)}
                placeholder="0 9 * * *"
                helperText="e.g., '0 9 * * *' for daily at 9 AM"
                sx={{ mb: 2 }}
              />
            )}
            
            {config.subtype === 'due_date_approaching' && (
              <TextField
                fullWidth
                type="number"
                label="Days Before Due Date"
                value={config.daysBefore || 1}
                onChange={(e) => handleFieldChange('daysBefore', parseInt(e.target.value) || 1)}
                helperText="Trigger this many days before the due date"
                sx={{ mb: 2 }}
                inputProps={{ min: 0, max: 30 }}
              />
            )}
          </>
        );

      case 'condition':
        const selectedField = taskFields.find(f => f.value === config.field);
        const fieldType = selectedField?.type || 'text';
        
        // Get appropriate operators based on field type
        const getOperatorsForFieldType = (type, fieldName) => {
          const operators = [];
          
          // Common operators
          operators.push({ value: 'is_empty', label: 'Is Empty' });
          operators.push({ value: 'is_not_empty', label: 'Is Not Empty' });
          
          switch(type) {
            case 'user':
              operators.unshift(
                { value: 'is', label: 'Is' },
                { value: 'is_not', label: 'Is Not' }
              );
              break;
              
            case 'users':
            case 'array':
              operators.unshift(
                { value: 'includes', label: 'Includes User' },
                { value: 'not_includes', label: 'Does Not Include' },
                { value: 'includes_any', label: 'Includes Any Of' },
                { value: 'includes_all', label: 'Includes All Of' }
              );
              break;
              
            case 'text':
            case 'tags':
              operators.unshift(
                { value: 'equals', label: 'Equals' },
                { value: 'not_equals', label: 'Not Equals' },
                { value: 'contains', label: 'Contains' },
                { value: 'not_contains', label: 'Does Not Contain' },
                { value: 'starts_with', label: 'Starts With' },
                { value: 'ends_with', label: 'Ends With' }
              );
              break;
              
            case 'number':
              operators.unshift(
                { value: 'equals', label: 'Equals' },
                { value: 'not_equals', label: 'Not Equals' },
                { value: 'greater_than', label: 'Greater Than' },
                { value: 'less_than', label: 'Less Than' },
                { value: 'greater_or_equal', label: 'Greater or Equal' },
                { value: 'less_or_equal', label: 'Less or Equal' },
                { value: 'between', label: 'Between' }
              );
              break;
              
            case 'date':
              operators.unshift(
                { value: 'is', label: 'Is' },
                { value: 'is_not', label: 'Is Not' },
                { value: 'before', label: 'Before' },
                { value: 'after', label: 'After' },
                { value: 'between', label: 'Between' },
                { value: 'within_days', label: 'Within X Days' },
                { value: 'past_days', label: 'In Past X Days' }
              );
              break;
              
            case 'boolean':
              operators.unshift(
                { value: 'is_true', label: 'Is True' },
                { value: 'is_false', label: 'Is False' }
              );
              break;
              
            case 'select':
            case 'reference':
              operators.unshift(
                { value: 'is', label: 'Is' },
                { value: 'is_not', label: 'Is Not' },
                { value: 'is_any_of', label: 'Is Any Of' },
                { value: 'is_none_of', label: 'Is None Of' }
              );
              break;
              
            default:
              operators.unshift(
                { value: 'equals', label: 'Equals' },
                { value: 'not_equals', label: 'Not Equals' }
              );
          }
          
          return operators;
        };
        
        const availableOperators = getOperatorsForFieldType(fieldType, config.field);
        
        return (
          <>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Field</InputLabel>
              <Select
                value={config.field || ''}
                label="Field"
                onChange={(e) => {
                  handleFieldChange('field', e.target.value);
                  // Reset operator when field changes
                  handleFieldChange('operator', '');
                  handleFieldChange('value', '');
                }}
              >
                {taskFields.map((field) => (
                  <MenuItem key={field.value} value={field.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography>{field.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        ({field.type})
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Operator</InputLabel>
              <Select
                value={config.operator || ''}
                label="Operator"
                onChange={(e) => handleFieldChange('operator', e.target.value)}
                disabled={!config.field}
              >
                {availableOperators.map((op) => (
                  <MenuItem key={op.value} value={op.value}>
                    {op.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {!['is_empty', 'is_not_empty', 'is_true', 'is_false'].includes(config.operator) && (
              <>
                {/* User field - show user picker */}
                {fieldType === 'user' ? (
                  <Autocomplete
                    fullWidth
                    value={projectMembers.find(u => u._id === config.value) || null}
                    onChange={(event, newValue) => {
                      handleFieldChange('value', newValue?._id || '');
                    }}
                    options={projectMembers}
                    getOptionLabel={(option) => option.name || option.email || ''}
                    renderOption={(props, option) => (
                      <ListItem {...props}>
                        <ListItemAvatar>
                          <Avatar src={option.profilePicture?.url} sx={{ width: 32, height: 32 }}>
                            {option.name?.[0] || option.email?.[0]}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText 
                          primary={option.name || 'Unnamed'}
                          secondary={option.email}
                        />
                      </ListItem>
                    )}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Select User" 
                        sx={{ mb: 2 }}
                        helperText={
                          loading ? "Loading team members..." : 
                          projectMembers.length === 0 ? "No team members found. Add team members to the project first." :
                          "Only project team members are shown"
                        }
                      />
                    )}
                    loading={loading}
                    disabled={loading}
                  />
                ) : 
                
                /* Users field (collaborators) - show multi-user picker */
                fieldType === 'users' || (fieldType === 'array' && config.field === 'collaborators') ? (
                  <Autocomplete
                    multiple
                    fullWidth
                    value={projectMembers.filter(u => 
                      (Array.isArray(config.value) ? config.value : []).includes(u._id)
                    )}
                    onChange={(event, newValue) => {
                      handleFieldChange('value', newValue.map(u => u._id));
                    }}
                    options={projectMembers}
                    getOptionLabel={(option) => option.name || option.email || ''}
                    renderOption={(props, option) => (
                      <ListItem {...props}>
                        <ListItemAvatar>
                          <Avatar src={option.profilePicture?.url} sx={{ width: 32, height: 32 }}>
                            {option.name?.[0] || option.email?.[0]}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText 
                          primary={option.name || 'Unnamed'}
                          secondary={option.email}
                        />
                      </ListItem>
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={option.name || option.email}
                          size="small"
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Select Users" 
                        sx={{ mb: 2 }}
                        helperText={
                          loading ? "Loading team members..." : 
                          projectMembers.length === 0 ? "No team members found. Add team members to the project first." :
                          "Select one or more project team members"
                        }
                      />
                    )}
                    loading={loading}
                    disabled={loading}
                  />
                ) : 
                
                /* Section field - show section dropdown */
                config.field === 'sectionId' ? (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Section</InputLabel>
                    <Select
                      value={config.value || ''}
                      label="Section"
                      onChange={(e) => handleFieldChange('value', e.target.value)}
                      disabled={loading}
                    >
                      <MenuItem value="">
                        <em>No Section</em>
                      </MenuItem>
                      {projectSections.map((section) => (
                        <MenuItem key={section._id} value={section._id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box 
                              sx={{ 
                                width: 12, 
                                height: 12, 
                                borderRadius: '50%', 
                                backgroundColor: section.color || '#666' 
                              }} 
                            />
                            {section.name}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) :
                
                /* Original field type handlers */
                fieldType === 'select' && config.field === 'status' ? (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Value</InputLabel>
                    <Select
                      value={config.value || ''}
                      label="Value"
                      onChange={(e) => handleFieldChange('value', e.target.value)}
                    >
                      {statusOptions.map((status) => (
                        <MenuItem key={status} value={status}>
                          {status.replace(/_/g, ' ').toUpperCase()}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : fieldType === 'select' && config.field === 'priority' ? (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Value</InputLabel>
                    <Select
                      value={config.value || ''}
                      label="Value"
                      onChange={(e) => handleFieldChange('value', e.target.value)}
                    >
                      {priorityOptions.map((priority) => (
                        <MenuItem key={priority} value={priority}>
                          {priority.toUpperCase()}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : fieldType === 'select' && config.field === 'type' ? (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Value</InputLabel>
                    <Select
                      value={config.value || ''}
                      label="Value"
                      onChange={(e) => handleFieldChange('value', e.target.value)}
                    >
                      {taskTypeOptions.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type.replace(/_/g, ' ').toUpperCase()}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : fieldType === 'select' && config.field === 'contentStage' ? (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Value</InputLabel>
                    <Select
                      value={config.value || ''}
                      label="Value"
                      onChange={(e) => handleFieldChange('value', e.target.value)}
                    >
                      {contentStageOptions.map((stage) => (
                        <MenuItem key={stage} value={stage}>
                          {stage.toUpperCase()}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : fieldType === 'date' ? (
                  <TextField
                    fullWidth
                    type="date"
                    label="Value"
                    value={config.value || ''}
                    onChange={(e) => handleFieldChange('value', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ mb: 2 }}
                  />
                ) : fieldType === 'number' ? (
                  <TextField
                    fullWidth
                    type="number"
                    label="Value"
                    value={config.value || ''}
                    onChange={(e) => handleFieldChange('value', e.target.value)}
                    sx={{ mb: 2 }}
                  />
                ) : config.operator === 'between' && fieldType === 'number' ? (
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="From"
                        value={config.value || ''}
                        onChange={(e) => handleFieldChange('value', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="To"
                        value={config.valueEnd || ''}
                        onChange={(e) => handleFieldChange('valueEnd', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                ) : config.operator === 'between' && fieldType === 'date' ? (
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        type="date"
                        label="From"
                        value={config.value || ''}
                        onChange={(e) => handleFieldChange('value', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        type="date"
                        label="To"
                        value={config.valueEnd || ''}
                        onChange={(e) => handleFieldChange('valueEnd', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>
                ) : (config.operator === 'within_days' || config.operator === 'past_days') ? (
                  <TextField
                    fullWidth
                    type="number"
                    label="Number of Days"
                    value={config.value || ''}
                    onChange={(e) => handleFieldChange('value', e.target.value)}
                    sx={{ mb: 2 }}
                    inputProps={{ min: 0 }}
                  />
                ) : fieldType === 'boolean' ? (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Value</InputLabel>
                    <Select
                      value={config.value || ''}
                      label="Value"
                      onChange={(e) => handleFieldChange('value', e.target.value)}
                    >
                      <MenuItem value="true">True</MenuItem>
                      <MenuItem value="false">False</MenuItem>
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    fullWidth
                    label="Value"
                    value={config.value || ''}
                    onChange={(e) => handleFieldChange('value', e.target.value)}
                    sx={{ mb: 2 }}
                  />
                )}
              </>
            )}
          </>
        );

      case 'action':
        return (
          <>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Action Type</InputLabel>
              <Select
                value={config.subtype || ''}
                label="Action Type"
                onChange={(e) => handleFieldChange('subtype', e.target.value)}
              >
                <MenuItem value="move_to_section">Move to Section</MenuItem>
                <MenuItem value="change_status">Change Status</MenuItem>
                <MenuItem value="change_priority">Change Priority</MenuItem>
                <MenuItem value="assign_task">Assign Task</MenuItem>
                <MenuItem value="unassign_task">Unassign Task</MenuItem>
                <MenuItem value="add_collaborator">Add Collaborator</MenuItem>
                <MenuItem value="remove_collaborator">Remove Collaborator</MenuItem>
                <MenuItem value="set_due_date">Set Due Date</MenuItem>
                <MenuItem value="clear_due_date">Clear Due Date</MenuItem>
                <MenuItem value="add_tag">Add Tag</MenuItem>
                <MenuItem value="remove_tag">Remove Tag</MenuItem>
                <MenuItem value="mark_complete">Mark Complete</MenuItem>
                <MenuItem value="mark_incomplete">Mark Incomplete</MenuItem>
                <MenuItem value="set_client_visibility">Set Client Visibility</MenuItem>
                <MenuItem value="create_subtask">Create Subtask</MenuItem>
                <MenuItem value="update_field">Update Custom Field</MenuItem>
                <MenuItem value="add_comment">Add Comment</MenuItem>
                <MenuItem value="send_notification">Send Notification</MenuItem>
                <MenuItem value="send_email">Send Email</MenuItem>
                <MenuItem value="webhook">Call Webhook</MenuItem>
              </Select>
            </FormControl>
            
            {renderActionFields()}
          </>
        );

      case 'delay':
        return (
          <>
            <TextField
              fullWidth
              type="number"
              label="Duration"
              value={config.duration || ''}
              onChange={(e) => handleFieldChange('duration', e.target.value)}
              sx={{ mb: 2 }}
            />
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Unit</InputLabel>
              <Select
                value={config.unit || 'minutes'}
                label="Unit"
                onChange={(e) => handleFieldChange('unit', e.target.value)}
              >
                <MenuItem value="seconds">Seconds</MenuItem>
                <MenuItem value="minutes">Minutes</MenuItem>
                <MenuItem value="hours">Hours</MenuItem>
                <MenuItem value="days">Days</MenuItem>
              </Select>
            </FormControl>
          </>
        );

      case 'loop':
        return (
          <>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Loop Type</InputLabel>
              <Select
                value={config.loopType || 'for_each'}
                label="Loop Type"
                onChange={(e) => handleFieldChange('loopType', e.target.value)}
              >
                <MenuItem value="for_each">For Each Item</MenuItem>
                <MenuItem value="while">While Condition</MenuItem>
                <MenuItem value="times">Fixed Times</MenuItem>
              </Select>
            </FormControl>
            
            {config.loopType === 'for_each' && (
              <TextField
                fullWidth
                label="Items Field"
                value={config.itemsField || ''}
                onChange={(e) => handleFieldChange('itemsField', e.target.value)}
                helperText="The field containing the array to loop through"
                sx={{ mb: 2 }}
              />
            )}
            
            {config.loopType === 'times' && (
              <TextField
                fullWidth
                type="number"
                label="Iterations"
                value={config.iterations || ''}
                onChange={(e) => handleFieldChange('iterations', e.target.value)}
                sx={{ mb: 2 }}
              />
            )}
          </>
        );

      default:
        return null;
    }
  };

  const renderActionFields = () => {
    switch (config.subtype) {
      case 'update_field':
        const updateField = taskFields.find(f => f.value === config.fieldName);
        const updateFieldType = updateField?.type || 'text';
        
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Field to Update</InputLabel>
                <Select
                  value={config.fieldName || ''}
                  label="Field to Update"
                  onChange={(e) => handleFieldChange('fieldName', e.target.value)}
                >
                  {taskFields.map((field) => (
                    <MenuItem key={field.value} value={field.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography>{field.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          ({field.type})
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              {updateFieldType === 'select' && config.fieldName === 'status' ? (
                <FormControl fullWidth>
                  <InputLabel>New Value</InputLabel>
                  <Select
                    value={config.fieldValue || ''}
                    label="New Value"
                    onChange={(e) => handleFieldChange('fieldValue', e.target.value)}
                  >
                    {statusOptions.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status.replace(/_/g, ' ').toUpperCase()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : updateFieldType === 'select' && config.fieldName === 'priority' ? (
                <FormControl fullWidth>
                  <InputLabel>New Value</InputLabel>
                  <Select
                    value={config.fieldValue || ''}
                    label="New Value"
                    onChange={(e) => handleFieldChange('fieldValue', e.target.value)}
                  >
                    {priorityOptions.map((priority) => (
                      <MenuItem key={priority} value={priority}>
                        {priority.toUpperCase()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : updateFieldType === 'date' ? (
                <TextField
                  fullWidth
                  type="date"
                  label="New Value"
                  value={config.fieldValue || ''}
                  onChange={(e) => handleFieldChange('fieldValue', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              ) : updateFieldType === 'number' ? (
                <TextField
                  fullWidth
                  type="number"
                  label="New Value"
                  value={config.fieldValue || ''}
                  onChange={(e) => handleFieldChange('fieldValue', e.target.value)}
                />
              ) : updateFieldType === 'boolean' ? (
                <FormControl fullWidth>
                  <InputLabel>New Value</InputLabel>
                  <Select
                    value={config.fieldValue || ''}
                    label="New Value"
                    onChange={(e) => handleFieldChange('fieldValue', e.target.value)}
                  >
                    <MenuItem value="true">True</MenuItem>
                    <MenuItem value="false">False</MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  fullWidth
                  label="New Value"
                  value={config.fieldValue || ''}
                  onChange={(e) => handleFieldChange('fieldValue', e.target.value)}
                />
              )}
            </Grid>
          </Grid>
        );

      case 'change_status':
        return (
          <FormControl fullWidth>
            <InputLabel>New Status</InputLabel>
            <Select
              value={config.newStatus || ''}
              label="New Status"
              onChange={(e) => handleFieldChange('newStatus', e.target.value)}
            >
              {statusOptions.map((status) => (
                <MenuItem key={status} value={status}>
                  {status.replace(/_/g, ' ').toUpperCase()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      
      case 'change_priority':
        return (
          <FormControl fullWidth>
            <InputLabel>New Priority</InputLabel>
            <Select
              value={config.newPriority || ''}
              label="New Priority"
              onChange={(e) => handleFieldChange('newPriority', e.target.value)}
            >
              {priorityOptions.map((priority) => (
                <MenuItem key={priority} value={priority}>
                  {priority.toUpperCase()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      
      case 'set_due_date':
        return (
          <>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Due Date Method</InputLabel>
              <Select
                value={config.dueDateMethod || 'specific'}
                label="Due Date Method"
                onChange={(e) => handleFieldChange('dueDateMethod', e.target.value)}
              >
                <MenuItem value="specific">Specific Date</MenuItem>
                <MenuItem value="offset">Days from Now</MenuItem>
              </Select>
            </FormControl>
            
            {config.dueDateMethod === 'offset' ? (
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Days"
                    value={config.dueDateOffset?.days || 0}
                    onChange={(e) => handleFieldChange('dueDateOffset', {
                      ...config.dueDateOffset,
                      days: parseInt(e.target.value) || 0
                    })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Hours"
                    value={config.dueDateOffset?.hours || 0}
                    onChange={(e) => handleFieldChange('dueDateOffset', {
                      ...config.dueDateOffset,
                      hours: parseInt(e.target.value) || 0
                    })}
                  />
                </Grid>
              </Grid>
            ) : (
              <TextField
                fullWidth
                type="date"
                label="Specific Date"
                value={config.specificDate || ''}
                onChange={(e) => handleFieldChange('specificDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            )}
          </>
        );
      
      case 'add_tag':
      case 'remove_tag':
        return (
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={Array.isArray(config.tags) ? config.tags : (config.tags ? [config.tags] : [])}
            onChange={(event, newValue) => {
              handleFieldChange('tags', newValue);
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip variant="outlined" label={option} {...getTagProps({ index })} />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={config.subtype === 'add_tag' ? 'Tags to Add' : 'Tags to Remove'}
                helperText="Type and press Enter to add tags"
              />
            )}
          />
        );
      
      case 'set_client_visibility':
        return (
          <FormControl fullWidth>
            <InputLabel>Client Visibility</InputLabel>
            <Select
              value={config.visible !== undefined ? config.visible.toString() : ''}
              label="Client Visibility"
              onChange={(e) => handleFieldChange('visible', e.target.value === 'true')}
            >
              <MenuItem value="true">Visible to Client</MenuItem>
              <MenuItem value="false">Hidden from Client</MenuItem>
            </Select>
          </FormControl>
        );
      
      case 'mark_complete':
      case 'mark_incomplete':
      case 'unassign_task':
      case 'clear_due_date':
        // These actions don't need additional configuration
        return (
          <Typography variant="body2" color="text.secondary">
            This action doesn't require additional configuration.
          </Typography>
        );
      
      case 'move_to_section':
        return (
          <FormControl fullWidth>
            <InputLabel>Target Section</InputLabel>
            <Select
              value={config.targetSectionId || ''}
              label="Target Section"
              onChange={(e) => handleFieldChange('targetSectionId', e.target.value)}
              disabled={loading}
            >
              <MenuItem value="">
                <em>No Section</em>
              </MenuItem>
              {projectSections.map((section) => (
                <MenuItem key={section._id} value={section._id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box 
                      sx={{ 
                        width: 12, 
                        height: 12, 
                        borderRadius: '50%', 
                        backgroundColor: section.color || '#666' 
                      }} 
                    />
                    {section.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      
      case 'assign_to':
      case 'assign_task':
        return (
          <>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Assignment Method</InputLabel>
              <Select
                value={config.assignmentMethod || 'specific_user'}
                label="Assignment Method"
                onChange={(e) => handleFieldChange('assignmentMethod', e.target.value)}
              >
                <MenuItem value="specific_user">Specific User</MenuItem>
                <MenuItem value="round_robin">Round Robin</MenuItem>
                <MenuItem value="least_workload">Least Workload</MenuItem>
                <MenuItem value="random">Random</MenuItem>
              </Select>
            </FormControl>
            
            {(config.assignmentMethod === 'specific_user' || !config.assignmentMethod) && (
              <Autocomplete
                fullWidth
                value={projectMembers.find(u => u._id === config.assignToUserId) || null}
                onChange={(event, newValue) => {
                  handleFieldChange('assignToUserId', newValue?._id || '');
                }}
                options={projectMembers}
                getOptionLabel={(option) => option.name || option.email || ''}
                renderOption={(props, option) => (
                  <ListItem {...props}>
                    <ListItemAvatar>
                      <Avatar src={option.profilePicture?.url} sx={{ width: 32, height: 32 }}>
                        {option.name?.[0] || option.email?.[0]}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText 
                      primary={option.name || 'Unnamed'}
                      secondary={option.email}
                    />
                  </ListItem>
                )}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Assign To User" 
                    helperText={
                      loading ? "Loading team members..." : 
                      projectMembers.length === 0 ? "No team members found. Add team members to the project first." :
                      "Select a project team member to assign the task to"
                    }
                  />
                )}
                loading={loading}
                disabled={loading}
              />
            )}
          </>
        );
      
      case 'add_collaborator':
        return (
          <>
            <Autocomplete
              fullWidth
              value={projectMembers.find(u => u._id === config.collaboratorUserId) || null}
              onChange={(event, newValue) => {
                handleFieldChange('collaboratorUserId', newValue?._id || '');
              }}
              options={projectMembers}
              getOptionLabel={(option) => option.name || option.email || ''}
              renderOption={(props, option) => (
                <ListItem {...props}>
                  <ListItemAvatar>
                    <Avatar src={option.profilePicture?.url} sx={{ width: 32, height: 32 }}>
                      {option.name?.[0] || option.email?.[0]}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText 
                    primary={option.name || 'Unnamed'}
                    secondary={option.email}
                  />
                </ListItem>
              )}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Collaborator" 
                  helperText={
                    loading ? "Loading team members..." : 
                    projectMembers.length === 0 ? "No team members found. Add team members to the project first." :
                    "Select a team member to add as collaborator"
                  }
                  sx={{ mb: 2 }}
                />
              )}
              loading={loading}
              disabled={loading}
            />
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Collaborator Status</InputLabel>
              <Select
                value={config.collaboratorStatus || 'to_do'}
                label="Collaborator Status"
                onChange={(e) => handleFieldChange('collaboratorStatus', e.target.value)}
              >
                <MenuItem value="to_do">To Do</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="done">Done</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              type="date"
              label="Deadline (Optional)"
              value={config.deadline || ''}
              onChange={(e) => handleFieldChange('deadline', e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Optional deadline for this collaborator"
            />
          </>
        );
      
      case 'remove_collaborator':
        return (
          <Autocomplete
            fullWidth
            value={projectMembers.find(u => u._id === config.collaboratorUserId) || null}
            onChange={(event, newValue) => {
              handleFieldChange('collaboratorUserId', newValue?._id || '');
            }}
            options={projectMembers}
            getOptionLabel={(option) => option.name || option.email || ''}
            renderOption={(props, option) => (
              <ListItem {...props}>
                <ListItemAvatar>
                  <Avatar src={option.profilePicture?.url} sx={{ width: 32, height: 32 }}>
                    {option.name?.[0] || option.email?.[0]}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText 
                  primary={option.name || 'Unnamed'}
                  secondary={option.email}
                />
              </ListItem>
            )}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Remove Collaborator" 
                helperText={
                  loading ? "Loading team members..." : 
                  projectMembers.length === 0 ? "No team members found. Add team members to the project first." :
                  "Select the collaborator to remove"
                }
              />
            )}
            loading={loading}
            disabled={loading}
          />
        );

      case 'send_email':
        return (
          <>
            <TextField
              fullWidth
              label="To (Email)"
              value={config.to || ''}
              onChange={(e) => handleFieldChange('to', e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Subject"
              value={config.subject || ''}
              onChange={(e) => handleFieldChange('subject', e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Body"
              value={config.body || ''}
              onChange={(e) => handleFieldChange('body', e.target.value)}
            />
          </>
        );

      case 'webhook':
        return (
          <>
            <TextField
              fullWidth
              label="Webhook URL"
              value={config.webhookUrl || ''}
              onChange={(e) => handleFieldChange('webhookUrl', e.target.value)}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>HTTP Method</InputLabel>
              <Select
                value={config.httpMethod || 'POST'}
                label="HTTP Method"
                onChange={(e) => handleFieldChange('httpMethod', e.target.value)}
              >
                <MenuItem value="GET">GET</MenuItem>
                <MenuItem value="POST">POST</MenuItem>
                <MenuItem value="PUT">PUT</MenuItem>
                <MenuItem value="DELETE">DELETE</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Payload (JSON)"
              value={config.payload || ''}
              onChange={(e) => {
                handleFieldChange('payload', e.target.value);
                // Validate JSON
                try {
                  if (e.target.value) JSON.parse(e.target.value);
                  handleFieldChange('payloadError', '');
                } catch (err) {
                  handleFieldChange('payloadError', 'Invalid JSON format');
                }
              }}
              error={!!config.payloadError}
              helperText={config.payloadError || 'Enter valid JSON payload'}
            />
          </>
        );

      case 'send_notification':
        return (
          <>
            <Autocomplete
              multiple
              fullWidth
              value={projectMembers.filter(u => 
                (Array.isArray(config.recipients) ? config.recipients : []).includes(u._id)
              )}
              onChange={(event, newValue) => {
                handleFieldChange('recipients', newValue.map(u => u._id));
              }}
              options={projectMembers}
              getOptionLabel={(option) => option.name || option.email || ''}
              renderOption={(props, option) => (
                <ListItem {...props}>
                  <ListItemAvatar>
                    <Avatar src={option.profilePicture?.url} sx={{ width: 32, height: 32 }}>
                      {option.name?.[0] || option.email?.[0]}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText 
                    primary={option.name || 'Unnamed'}
                    secondary={option.email}
                  />
                </ListItem>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option.name || option.email}
                    size="small"
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Recipients" 
                  helperText="Select users to notify"
                  sx={{ mb: 2 }}
                />
              )}
              loading={loading}
              disabled={loading}
            />
            
            <TextField
              fullWidth
              label="Notification Title"
              value={config.title || ''}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Message"
              value={config.message || ''}
              onChange={(e) => handleFieldChange('message', e.target.value)}
              helperText="Use {{task.title}}, {{task.assignedTo.name}} for dynamic values"
            />
          </>
        );
      
      case 'add_comment':
        return (
          <>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Comment"
              value={config.comment || ''}
              onChange={(e) => handleFieldChange('comment', e.target.value)}
              helperText="Comment will be added to the task"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={config.internal || false}
                  onChange={(e) => handleFieldChange('internal', e.target.checked)}
                />
              }
              label="Internal Comment (not visible to clients)"
              sx={{ mt: 2 }}
            />
          </>
        );
      
      case 'create_subtask':
        return (
          <>
            <TextField
              fullWidth
              label="Subtask Title"
              value={config.taskTemplate?.title || ''}
              onChange={(e) => handleFieldChange('taskTemplate', {
                ...config.taskTemplate,
                title: e.target.value
              })}
              sx={{ mb: 2 }}
              required
            />
            
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Description"
              value={config.taskTemplate?.description || ''}
              onChange={(e) => handleFieldChange('taskTemplate', {
                ...config.taskTemplate,
                description: e.target.value
              })}
              sx={{ mb: 2 }}
            />
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Assign To</InputLabel>
              <Select
                value={config.taskTemplate?.assignTo || 'same_as_parent'}
                label="Assign To"
                onChange={(e) => handleFieldChange('taskTemplate', {
                  ...config.taskTemplate,
                  assignTo: e.target.value
                })}
              >
                <MenuItem value="same_as_parent">Same as Parent Task</MenuItem>
                <MenuItem value="specific_user">Specific User</MenuItem>
                <MenuItem value="unassigned">Unassigned</MenuItem>
              </Select>
            </FormControl>
            
            {config.taskTemplate?.assignTo === 'specific_user' && (
              <Autocomplete
                fullWidth
                value={projectMembers.find(u => u._id === config.taskTemplate?.assignToUserId) || null}
                onChange={(event, newValue) => {
                  handleFieldChange('taskTemplate', {
                    ...config.taskTemplate,
                    assignToUserId: newValue?._id || ''
                  });
                }}
                options={projectMembers}
                getOptionLabel={(option) => option.name || option.email || ''}
                renderOption={(props, option) => (
                  <ListItem {...props}>
                    <ListItemAvatar>
                      <Avatar src={option.profilePicture?.url} sx={{ width: 32, height: 32 }}>
                        {option.name?.[0] || option.email?.[0]}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText 
                      primary={option.name || 'Unnamed'}
                      secondary={option.email}
                    />
                  </ListItem>
                )}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Assign To User" 
                    sx={{ mb: 2 }}
                  />
                )}
                loading={loading}
                disabled={loading}
              />
            )}
            
            <TextField
              fullWidth
              type="number"
              label="Due In Days (Optional)"
              value={config.taskTemplate?.dueInDays || ''}
              onChange={(e) => handleFieldChange('taskTemplate', {
                ...config.taskTemplate,
                dueInDays: parseInt(e.target.value) || 0
              })}
              helperText="Number of days from now for the due date"
              inputProps={{ min: 0 }}
            />
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Configure {node?.type ? node.type.charAt(0).toUpperCase() + node.type.slice(1) : 'Node'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="Label"
            value={config.label || node?.data?.label || ''}
            onChange={(e) => handleFieldChange('label', e.target.value)}
            sx={{ mb: 2 }}
          />
          
          <TextField
            fullWidth
            label="Description"
            value={config.description || ''}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />
          
          {renderConfigFields()}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default NodeConfigDialog;